/** UAT administrator draft controls; generation never activates a questionnaire.
 * Keep existing V1 simulation separate from Meta asset validation and publication.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import { Button, Message } from 'semantic-ui-react';

const errors = {
  candidate_changed_generate_again:
    'The questionnaire changed. Generate and validate its draft again before approval.',
  approval_not_activated:
    'Activation was not completed. Refresh the questionnaire and retry approval after checking its draft.',
  meta_candidate_not_ready: 'Meta has not confirmed this Flow is ready for publication.',
  publication_not_confirmed: 'Publication was not confirmed. Reconcile its Meta status before retrying.',
  publication_requires_operator_check: 'The previous publication request needs an operator check.',
  immutable_package_requires_operator_check:
    'The retained package needs an operator check before activation.',
  deactivate_before_generating: 'Deactivate the questionnaire before generating a draft.',
  translation_not_ready: 'Finish translating the questionnaire before generating a draft.',
  questionnaire_outside_pilot: 'Draft generation is not yet enabled for this questionnaire.',
  forbidden: 'Draft management currently requires a TenantZero administrator.',
  source_changed_during_generation: 'The questionnaire changed during generation. Generate it again.',
  operator_reconciliation_required: 'The previous operation needs an operator check before retrying.',
  generation_queue_uncertain: 'The request may have started. Refresh its status before trying again.',
};
const labels = {
  not_generated: 'Not generated',
  publication_queued: 'Approval queued',
  publishing: 'Publishing',
  published: 'Published',
  published_outdated: 'Published; activation not completed',
  publication_uncertain: 'Publication needs reconciliation',
  queued: 'Queued',
  generating: 'Generating',
  ready: 'Last generated draft validated',
  outdated: 'Changes pending',
  validation_failed: 'Validation failed',
  failed: 'Generation failed',
  reconciliation_required: 'Operator check required',
};
export function FlowDraftPanel({ questionnaire, token, isTenantZero }) {
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const polling = useRef({ operationId: null, started: 0 });
  const enabled =
    process.env.REACT_APP_FLOW_DRAFT_ADMIN === 'true' && isTenantZero && questionnaire.id && token;
  const request = useCallback(
    async (method = 'get') => {
      setBusy(true);
      setError('');
      try {
        const response = await axios({
          method,
          url: `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_ENV}/whatsapp-flows/draft`,
          params: { tenantId: questionnaire.tenantId, questionnaireId: questionnaire.id },
          headers: { Authorization: `Bearer ${token}` },
        });
        setResult(response.data);
      } catch (e) {
        const code = e.response?.data?.error;
        setError(
          errors[code] || 'Could not retrieve the draft operation. Refresh its status before retrying.',
        );
      } finally {
        setBusy(false);
      }
    },
    [questionnaire.id, questionnaire.tenantId, token],
  );
  useEffect(() => {
    setResult(null);
    if (enabled) request();
  }, [enabled, request]);
  useEffect(() => {
    if (!enabled || !['queued', 'generating', 'publication_queued', 'publishing'].includes(result?.status))
      return undefined;
    if (polling.current.operationId !== result.operationId) {
      polling.current = { operationId: result.operationId, started: Date.now() };
    }
    // A stalled worker needs inspection; do not poll indefinitely in an open tab.
    if (Date.now() - polling.current.started > 300000) return undefined;
    const timer = setTimeout(() => request(), 5000);
    return () => clearTimeout(timer);
  }, [enabled, result, request]);
  if (!enabled) return null;
  const active = questionnaire.isActive || questionnaire.status === 'active' || result?.active;
  const pending = ['queued', 'generating', 'publication_queued', 'publishing'].includes(result?.status);
  return (
    <div>
      <p>
        Generate a draft from the saved questionnaire and its translations. This does not activate or publish
        it. The existing Test action simulates questionnaire logic; it does not preview WhatsApp screens.
      </p>
      {result && (
        <p>
          <strong>{labels[result.status] || result.status}</strong>
          {result.version ? ` · Version ${result.version}` : ''}
          {result.metaStatus ? ` · Meta: ${result.metaStatus}` : ''}
        </p>
      )}
      {result?.currentBinding && (
        <p>
          <strong>Selected respondent Flow</strong>: {result.currentBinding.mode || 'Unknown status'}
          {result.currentBinding.version ? ` · Version ${result.currentBinding.version}` : ''}
          {result.currentBinding.flowId ? ` · ID ${result.currentBinding.flowId}` : ''}
          {result.active && result.currentBinding.enabled && result.currentBinding.metaValidated
            ? ' · Enabled binding (source checked at launch)'
            : ' · Not enabled for new starts'}
          {result.currentBinding.managementUrl && (
            <>
              {' '}
              ·{' '}
              <a href={result.currentBinding.managementUrl} target="_blank" rel="noopener noreferrer">
                Open selected Flow in Meta
              </a>
            </>
          )}
        </p>
      )}
      {result?.updatedAt && (
        <p>Last operation: {result.updatedAt}. Later edits are checked when generating again.</p>
      )}
      {active && <p>Deactivate and save this questionnaire before generating its draft.</p>}
      {error && <Message negative content={error} />}
      {result?.status === "validation_failed" && (
        <Message negative content="The latest changes were not uploaded to Meta. Correct the validation errors and generate again. Any Meta link below opens the previously generated draft." />
      )}
      {result?.errorCode && (
        <Message
          warning
          content={
            errors[result.errorCode] ||
            'Generation could not finish. Review the diagnostics or ask an administrator to inspect the operation.'
          }
        />
      )}
      {(result?.diagnostics || []).map((d, i) => (
        <Message warning key={i} content={`${d.recordId ? `${d.recordId}: ` : ''}${d.message || d.code}`} />
      ))}
      <Button
        type="button"
        disabled={
          busy ||
          active ||
          pending ||
          ['reconciliation_required', 'publication_uncertain'].includes(result?.status)
        }
        loading={busy && !pending}
        onClick={() => request('post')}
      >
        Generate / update draft Flow
      </Button>
      <Button type="button" disabled={busy} onClick={() => request()}>
        Refresh status
      </Button>
      {result?.managementUrl && (
        <p>
          <a href={result.managementUrl} target="_blank" rel="noopener noreferrer">
            Open Flow in Meta
          </a>{' '}
          — Meta access is required.
        </p>
      )}
    </div>
  );
}
export default connect((state) => {
  const session = state.authData?.[0]?.signInUserSession;
  return {
    token: session?.accessToken?.jwtToken,
    isTenantZero: (session?.accessToken?.payload?.['cognito:groups'] || []).includes('TenantZero'),
  };
})(FlowDraftPanel);
